import { Button, Card, MenuItem, Stack, TextField, Typography } from '@mui/material'
import { useState } from 'react'
import { toast } from 'react-toastify'
import { useParams } from 'react-router-dom'
import {
  useCreateSocialOrderRestrictionMutation,
  useDeleteSocialOrderRestrictionMutation,
  useGetSocialOrderQuery,
  useGetSocialOrderRestrictionsQuery,
} from '../../../../../api/socialOrders'
import type {
  ISocialOrderUserRestrictionResource,
  SocialOrderUserRestrictionLevel,
} from '../../../../../api/types/socialOrders'
import {
  editProjectFieldSx,
  editProjectSelectFieldSx,
} from '../../../EditProject/Tabs/editProjectFieldSx'
import {
  buildRestrictionLevelLabels,
  formatRestrictionUserLabel,
  formatRestrictionUserSlug,
  SocialOrderRestrictionUserPicker,
  type SelectedRestrictionUser,
} from '../../../../../components/Moderation/RestrictionUserPicker'
import { extractApiErrorMessage } from '../../../../../utils/api'

const levelLabels = buildRestrictionLevelLabels('Скрыть соцзаказ')

export const EditSocialOrderAdministrationForm = () => {
  const { id } = useParams<{ id: string }>()
  const socialOrderId = Number(id)
  const { data: socialOrder } = useGetSocialOrderQuery(
    { id: socialOrderId },
    { skip: !id || Number.isNaN(socialOrderId) },
  )
  const permissions = socialOrder?.viewer?.permissions
  const canManage = permissions?.can_edit_content === true

  const { data: restrictions = [], refetch: refetchRestrictions } = useGetSocialOrderRestrictionsQuery(
    { socialOrderId },
    { skip: !canManage || !socialOrderId },
  )

  const [createRestriction, createRestrictionState] = useCreateSocialOrderRestrictionMutation()
  const [deleteRestriction] = useDeleteSocialOrderRestrictionMutation()

  const [selectedUser, setSelectedUser] = useState<SelectedRestrictionUser | null>(null)
  const [restrictionLevel, setRestrictionLevel] = useState<SocialOrderUserRestrictionLevel>('comments')
  const [restrictionReason, setRestrictionReason] = useState('')
  const [restrictionSubmitError, setRestrictionSubmitError] = useState<string | null>(null)

  const handleAddRestriction = async () => {
    if (!selectedUser) {
      toast.info('Укажите имя профиля (@...) или выберите из подсказок')
      return
    }

    setRestrictionSubmitError(null)
    try {
      await createRestriction({
        socialOrderId,
        body: {
          user_id: selectedUser.id,
          level: restrictionLevel,
          ...(restrictionReason.trim() ? { reason: restrictionReason.trim() } : {}),
        },
      }).unwrap()
      toast.success('Ограничение добавлено')
      setSelectedUser(null)
      setRestrictionReason('')
      void refetchRestrictions()
    } catch (error) {
      const message = extractApiErrorMessage(error) ?? 'Не удалось добавить ограничение'
      setRestrictionSubmitError(message)
      toast.error(message)
    }
  }

  const handleRemoveRestriction = async (item: ISocialOrderUserRestrictionResource) => {
    try {
      await deleteRestriction({ socialOrderId, userId: item.user_id }).unwrap()
      toast.success('Ограничение снято')
    } catch {
      toast.error('Не удалось снять ограничение')
    }
  }

  if (!canManage) {
    return (
      <Typography variant="black-14-light" color="text.secondary">
        Недостаточно прав для управления ограничениями.
      </Typography>
    )
  }

  return (
    <Stack gap={2}>
      <Card variant="primaryAdaptiveCard" sx={{ p: 2.5 }}>
        <Stack gap={2}>
          <Typography variant="black-16-semiBold">Ограничения пользователей</Typography>
          <Typography variant="black-14-light" color="text.secondary">
            Укажите пользователя, уровень ограничения и нажмите «Добавить ограничение».
          </Typography>

          <SocialOrderRestrictionUserPicker
            socialOrderId={socialOrderId}
            selectedUser={selectedUser}
            onSelectedUserChange={(user) => {
              setSelectedUser(user)
              if (user) setRestrictionSubmitError(null)
            }}
            submitError={restrictionSubmitError}
          />

          <Stack gap={0.5}>
            <Typography variant="black-14-medium">Уровень ограничения</Typography>
            <TextField
              select
              size="small"
              value={restrictionLevel}
              onChange={(e) => setRestrictionLevel(e.target.value as SocialOrderUserRestrictionLevel)}
              sx={{ ...editProjectSelectFieldSx, width: { xs: '100%', sm: 280 } }}
              SelectProps={{
                MenuProps: {
                  disableScrollLock: true,
                },
              }}
            >
              <MenuItem value="comments">{levelLabels.comments}</MenuItem>
              <MenuItem value="full">{levelLabels.full}</MenuItem>
            </TextField>
          </Stack>

          <Stack gap={0.5}>
            <Typography variant="black-14-medium">Причина (необязательно)</Typography>
            <TextField
              size="small"
              value={restrictionReason}
              onChange={(e) => setRestrictionReason(e.target.value)}
              placeholder="Кратко, почему ограничение"
              inputProps={{ maxLength: 500 }}
              sx={editProjectFieldSx}
            />
          </Stack>

          <Stack direction="row">
            <Button
              variant="follow"
              onClick={() => void handleAddRestriction()}
              disabled={createRestrictionState.isLoading || !selectedUser}
            >
              Добавить ограничение
            </Button>
          </Stack>

          {restrictions.length === 0 && (
            <Typography variant="black-14-light">Ограничений пока нет.</Typography>
          )}
          {restrictions.map((item) => {
            const name = formatRestrictionUserLabel(item.user)
            const slug = formatRestrictionUserSlug(item.user)
            return (
              <Stack
                key={`${item.user_id}-${item.level}`}
                direction={{ xs: 'column', sm: 'row' }}
                gap={1}
                alignItems={{ sm: 'center' }}
                justifyContent="space-between"
              >
                <Stack>
                  <Typography variant="black-14-normal">{name}</Typography>
                  <Typography variant="black-12-light" color="text.secondary">
                    {slug ? `${slug} · ` : ''}
                    {levelLabels[item.level]}
                    {item.reason ? ` · ${item.reason}` : ''}
                  </Typography>
                </Stack>
                <Button variant="danger" size="small" onClick={() => void handleRemoveRestriction(item)}>
                  Снять
                </Button>
              </Stack>
            )
          })}
        </Stack>
      </Card>
    </Stack>
  )
}
